/**
 * BoekingForm - Nieuwe of bewerken boeking
 * Gebaseerd op Morris boeking_form.html
 */

import { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import BoekhoudingLayout from '../../components/boekhouding/BoekhoudingLayout';
import styles from '../../styles/Boekhouding.module.css';
import {
  boekhoudingService,
  Boeking,
  BoekingCreate,
  BoekstukType,
  Grootboekrekening,
  GrootboekType,
  RelatieType,
} from '../../services/boekhouding.service';

interface Relatie {
  id: number;
  naam: string;
  type: string;
}

const BoekingForm = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const bewerkModus = !!id;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form data
  const [boekstuk, setBoekstuk] = useState<BoekstukType>(
    (searchParams.get('boekstuk') as BoekstukType) || 'inkoop'
  );
  const [datum, setDatum] = useState(new Date().toISOString().split('T')[0]);
  const [bedragIncl, setBedragIncl] = useState('');
  const [btwPercentage, setBtwPercentage] = useState(21);
  const [handmatigeBtw, setHandmatigeBtw] = useState(false);
  const [bedragExclHandmatig, setBedragExclHandmatig] = useState('');
  const [btwBedragHandmatig, setBtwBedragHandmatig] = useState('');
  const [bedragInclHandmatig, setBedragInclHandmatig] = useState('');
  const [grootboeknaam, setGrootboeknaam] = useState(searchParams.get('grootboek') || '');
  const [relatieId, setRelatieId] = useState<number | null>(null);
  const [boekstuknummer, setBoekstuknummer] = useState('');
  const [externFactuurnummer, setExternFactuurnummer] = useState('');
  const [notitie, setNotitie] = useState('');
  // Bij kas en bank bepaalt dit of het geld binnenkomt of weggaat. Stond eerder
  // vast op "af" voor kas, waardoor een storting het kassaldo verlaagde.
  const [isCredit, setIsCredit] = useState(false);
  // Betaling op een factuurboeking: komt binnen via ?factuur_id= vanaf de
  // knop "Betaling toevoegen" op een inkoop- of verkoopboeking.
  const [factuurBoeking, setFactuurBoeking] = useState<Boeking | null>(null);
  // Bij bewerken ligt het boekstuk alleen vast voor inkoop en verkoop: die hangen
  // aan een factuur of een scan.
  const boekstukVast = bewerkModus && (boekstuk === 'inkoop' || boekstuk === 'verkoop');

  // Dropdown data
  const [grootboeken, setGrootboeken] = useState<Grootboekrekening[]>([]);
  const [relaties, setRelaties] = useState<Relatie[]>([]);

  // Nieuwe relatie form
  const [showNieuweRelatie, setShowNieuweRelatie] = useState(false);
  const [nieuweRelatieNaam, setNieuweRelatieNaam] = useState('');
  const [nieuweRelatieType, setNieuweRelatieType] = useState<RelatieType>(RelatieType.CREDITEUR);

  // Huidig boekjaar uit localStorage
  const huidigJaar = parseInt(localStorage.getItem('boekhouding_jaar') || new Date().getFullYear().toString());

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        // Laad grootboekrekeningen
        const gbResponse = await boekhoudingService.getGrootboekrekeningen();
        setGrootboeken(gbResponse.items);

        // Laad relaties (boekhouding relaties endpoint)
        try {
          const data = await boekhoudingService.getRelaties();
          setRelaties(data.items || []);
        } catch (e) {
          console.error('Error loading relaties:', e);
        }

        // Als bewerken, laad boeking
        if (bewerkModus && id) {
          const boeking = await boekhoudingService.getBoeking(parseInt(id));
          setBoekstuk(boeking.boekstuk);
          setDatum(boeking.datum);
          setBedragIncl(boeking.bedrag_incl.toString());
          setBtwPercentage(boeking.btw_percentage || 21);
          setGrootboeknaam(boeking.grootboeknaam);
          setRelatieId(boeking.relatie_id || null);
          setBoekstuknummer(boeking.boekstuknummer || '');
          setExternFactuurnummer(boeking.extern_factuurnummer || '');
          setNotitie(boeking.notitie || '');
          setIsCredit(!!boeking.is_credit);
        }

        // Nieuwe betaling op een bestaande factuurboeking: alles voorvullen
        const factuurIdParam = searchParams.get('factuur_id');
        if (!bewerkModus && factuurIdParam) {
          const factuur = await boekhoudingService.getBoeking(parseInt(factuurIdParam));
          setFactuurBoeking(factuur);
          // Het openstaande saldo, niet het factuurbedrag: bij een tweede
          // deelbetaling staat er anders te veel.
          const openstaand = Number(factuur.saldo ?? factuur.bedrag_incl) || 0;
          setBedragIncl(openstaand.toFixed(2));
          // Een betaling is btw-neutraal; de btw zit al in de factuurboeking.
          setBtwPercentage(0);
          setGrootboeknaam(factuur.grootboeknaam);
          setRelatieId(factuur.relatie_id || null);
          // Inkoop betalen gaat eraf, een verkoopfactuur komt binnen.
          setIsCredit(factuur.boekstuk === 'verkoop');
        }
      } catch (err) {
        console.error('Error loading data:', err);
        setError('Fout bij laden van gegevens');
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [bewerkModus, id]);

  // Bereken BTW bedragen
  const berekenBedragen = () => {
    if (handmatigeBtw) {
      return {
        bedrag_excl: parseFloat(bedragExclHandmatig) || 0,
        btw_bedrag: parseFloat(btwBedragHandmatig) || 0,
        bedrag_incl: parseFloat(bedragInclHandmatig) || 0,
      };
    }

    const incl = parseFloat(bedragIncl) || 0;
    const percentage = btwPercentage;
    const excl = incl / (1 + percentage / 100);
    const btw = incl - excl;

    return {
      bedrag_excl: Math.round(excl * 100) / 100,
      btw_bedrag: Math.round(btw * 100) / 100,
      bedrag_incl: incl,
    };
  };

  // Grootboekrekeningen waarbij het geld binnenkomt in plaats van weggaat.
  // Alleen een voorzet: de keuze "Soort" blijft met de hand te wijzigen.
  const KOMT_BINNEN = ['storting', 'teruggave', 'ontvangst'];

  // Update BTW percentage bij grootboek selectie
  const handleGrootboekChange = (value: string) => {
    setGrootboeknaam(value);
    const selected = grootboeken.find(gb => gb.grootboeknaam === value);
    if (selected && selected.btw_percentage !== undefined) {
      setBtwPercentage(selected.btw_percentage);
    }
    if (boekstuk === 'kas' || boekstuk === 'bank' || boekstuk === 'memoriaal' || boekstuk === 'lening') {
      const naam = value.toLowerCase();
      setIsCredit(KOMT_BINNEN.some(woord => naam.includes(woord)));
    }
  };

  // Nieuwe relatie aanmaken
  const maakNieuweRelatie = async () => {
    if (!nieuweRelatieNaam.trim()) {
      alert('Vul een naam in');
      return;
    }

    try {
      const data = await boekhoudingService.createRelatie({
        naam: nieuweRelatieNaam,
        type: nieuweRelatieType,
      });
      setRelaties([...relaties, data]);
      setRelatieId(data.id);
      setNieuweRelatieNaam('');
      setShowNieuweRelatie(false);
    } catch (e) {
      console.error('Error creating relatie:', e);
      alert('Fout bij aanmaken relatie');
    }
  };

  // Form submit
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      const bedragen = berekenBedragen();

      const data: BoekingCreate = {
        jaar: huidigJaar,
        datum,
        boekstuk,
        boekstuknummer: boekstuknummer || undefined,
        omschrijving: factuurBoeking
          ? `Betaling ${factuurBoeking.boekstuknummer || ''} - ${factuurBoeking.relatie_naam || grootboeknaam}`.trim()
          : relaties.find(r => r.id === relatieId)?.naam || notitie || grootboeknaam,
        bedrag_excl: bedragen.bedrag_excl,
        btw_percentage: handmatigeBtw ? 0 : btwPercentage,
        btw_bedrag: bedragen.btw_bedrag,
        bedrag_incl: bedragen.bedrag_incl,
        grootboeknaam,
        relatie_id: relatieId || undefined,
        notitie: notitie || undefined,
        extern_factuurnummer: externFactuurnummer || undefined,
        // Verkoop is altijd een vordering; inkoop gaat er altijd af.
        // Alleen bij kas en bank kiest de gebruiker de richting.
        gekoppeld_aan_id: factuurBoeking?.id,
        is_credit: boekstuk === 'verkoop' ? true
          : (boekstuk === 'kas' || boekstuk === 'bank' || boekstuk === 'memoriaal' || boekstuk === 'lening') ? isCredit
          : false,
      };

      if (bewerkModus && id) {
        await boekhoudingService.updateBoeking(parseInt(id), data);
        // Terug naar detail pagina
        navigate(`/boekhouding/boeking/${id}`);
      } else {
        const created = await boekhoudingService.createBoeking(data);
        // Naar detail pagina van nieuwe boeking
        navigate(`/boekhouding/boeking/${created.id}`);
      }
    } catch (err: any) {
      console.error('Error saving boeking:', err);
      setError(err.message || 'Fout bij opslaan');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <BoekhoudingLayout title={bewerkModus ? 'Boeking Bewerken' : 'Nieuwe Boeking'}>
        <div className={styles.loading}>Laden...</div>
      </BoekhoudingLayout>
    );
  }

  // Filter grootboeken op basis van boekstuk type
  const filteredGrootboeken = grootboeken.filter(gb => {
    if (boekstuk === 'inkoop') return gb.type === GrootboekType.INKOOP || gb.type === GrootboekType.KOSTEN;
    if (boekstuk === 'verkoop') return gb.type === GrootboekType.OMZET;
    if (boekstuk === 'lening') return gb.grootboeknaam.toLowerCase().includes('lening');
    return true; // kas/bank kunnen alles
  });

  return (
    <BoekhoudingLayout title={bewerkModus ? 'Boeking Bewerken' : 'Nieuwe Boeking'}>
      <div className={`${styles.card} ${styles.cardForm}`}>
        <form onSubmit={handleSubmit} className={styles.form}>
          {error && <div className={`${styles.alert} ${styles.alertDanger}`}>{error}</div>}

          {factuurBoeking && (
            <div className={`${styles.alert} ${styles.alertInfo}`}>
              Betaling op {factuurBoeking.boekstuk} {factuurBoeking.boekstuknummer}
              {factuurBoeking.relatie_naam ? ` - ${factuurBoeking.relatie_naam}` : ''}.
              Openstaand: &euro;{' '}
              {new Intl.NumberFormat('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                .format(Number(factuurBoeking.saldo ?? factuurBoeking.bedrag_incl) || 0)}
            </div>
          )}

          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label htmlFor="boekstuk">Boekstuk</label>
              <select
                id="boekstuk"
                value={boekstuk}
                onChange={(e) => !boekstukVast && setBoekstuk(e.target.value as BoekstukType)}
                disabled={boekstukVast}
              >
                <option value="kas">Kas</option>
                <option value="bank">Bank</option>
                <option value="inkoop">Inkoop</option>
                <option value="verkoop">Verkoop</option>
                <option value="memoriaal">Memoriaal</option>
                <option value="lening">Leningen</option>
              </select>
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="datum">Datum</label>
              <input
                type="date"
                id="datum"
                value={datum}
                onChange={(e) => setDatum(e.target.value)}
                required
              />
            </div>

            {(boekstuk === 'kas' || boekstuk === 'bank' || boekstuk === 'memoriaal' || boekstuk === 'lening') && (
              <div className={styles.formGroup}>
                <label htmlFor="richting">Soort</label>
                <select
                  id="richting"
                  value={isCredit ? 'bij' : 'af'}
                  onChange={(e) => setIsCredit(e.target.value === 'bij')}
                >
                  <option value="af">Uitgave (af)</option>
                  <option value="bij">Ontvangst (bij)</option>
                </select>
              </div>
            )}
          </div>

          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label htmlFor="bedrag_incl">Bedrag (incl. BTW)</label>
              <input
                type="number"
                id="bedrag_incl"
                value={bedragIncl}
                onChange={(e) => setBedragIncl(e.target.value)}
                step="0.01"
                placeholder="0.00"
                required={!handmatigeBtw}
                disabled={handmatigeBtw}
                style={{ opacity: handmatigeBtw ? 0.5 : 1 }}
              />
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="btw_percentage">BTW %</label>
              <select
                id="btw_percentage"
                value={btwPercentage}
                onChange={(e) => setBtwPercentage(parseInt(e.target.value))}
                disabled={handmatigeBtw}
                style={{ opacity: handmatigeBtw ? 0.5 : 1 }}
              >
                <option value={21}>21%</option>
                <option value={9}>9%</option>
                <option value={0}>0%</option>
              </select>
            </div>
          </div>

          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={handmatigeBtw}
                  onChange={(e) => {
                    setHandmatigeBtw(e.target.checked);
                    if (e.target.checked) {
                      setBedragInclHandmatig(bedragIncl);
                    }
                  }}
                />
                Handmatige BTW invoer
              </label>
            </div>
          </div>

          {handmatigeBtw && (
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label htmlFor="bedrag_excl_handmatig">Bedrag excl. BTW</label>
                <input
                  type="number"
                  id="bedrag_excl_handmatig"
                  value={bedragExclHandmatig}
                  onChange={(e) => setBedragExclHandmatig(e.target.value)}
                  step="0.01"
                  placeholder="0.00"
                />
              </div>
              <div className={styles.formGroup}>
                <label htmlFor="btw_bedrag_handmatig">BTW bedrag</label>
                <input
                  type="number"
                  id="btw_bedrag_handmatig"
                  value={btwBedragHandmatig}
                  onChange={(e) => setBtwBedragHandmatig(e.target.value)}
                  step="0.01"
                  placeholder="0.00"
                />
              </div>
              <div className={styles.formGroup}>
                <label htmlFor="bedrag_incl_handmatig">Bedrag incl. BTW</label>
                <input
                  type="number"
                  id="bedrag_incl_handmatig"
                  value={bedragInclHandmatig}
                  onChange={(e) => setBedragInclHandmatig(e.target.value)}
                  step="0.01"
                  placeholder="0.00"
                />
              </div>
            </div>
          )}

          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label htmlFor="grootboeknaam">Grootboekrekening</label>
              <select
                id="grootboeknaam"
                value={grootboeknaam}
                onChange={(e) => handleGrootboekChange(e.target.value)}
                required
              >
                <option value="">-- Selecteer grootboekrekening --</option>
                {filteredGrootboeken.map(gb => (
                  <option key={gb.grootboeknaam} value={gb.grootboeknaam}>
                    {gb.grootboeknaam}
                  </option>
                ))}
              </select>
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="relatie_id">Relatie (optioneel)</label>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <select
                  id="relatie_id"
                  value={relatieId || ''}
                  onChange={(e) => setRelatieId(e.target.value ? parseInt(e.target.value) : null)}
                  style={{ flex: 1 }}
                >
                  <option value="">-- Geen relatie --</option>
                  {relaties.map(r => (
                    <option key={r.id} value={r.id}>{r.naam}</option>
                  ))}
                </select>
                <button
                  type="button"
                  className={`${styles.btn} ${styles.btnSecondary} ${styles.btnSm}`}
                  onClick={() => setShowNieuweRelatie(!showNieuweRelatie)}
                >
                  + Nieuw
                </button>
              </div>

              {showNieuweRelatie && (
                <div style={{
                  marginTop: '0.5rem',
                  padding: '0.75rem',
                  background: 'var(--gray-100)',
                  borderRadius: '4px'
                }}>
                  <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                    <input
                      type="text"
                      value={nieuweRelatieNaam}
                      onChange={(e) => setNieuweRelatieNaam(e.target.value)}
                      placeholder="Naam relatie"
                      style={{ flex: 1, minWidth: '150px' }}
                    />
                    <select
                      value={nieuweRelatieType}
                      onChange={(e) => setNieuweRelatieType(e.target.value as RelatieType)}
                      style={{ width: 'auto' }}
                    >
                      <option value="crediteur">Leverancier</option>
                      <option value="debiteur">Klant</option>
                      <option value="beide">Beide</option>
                    </select>
                    <button
                      type="button"
                      className={`${styles.btn} ${styles.btnPrimary} ${styles.btnSm}`}
                      onClick={maakNieuweRelatie}
                    >
                      Toevoegen
                    </button>
                    <button
                      type="button"
                      className={`${styles.btn} ${styles.btnSecondary} ${styles.btnSm}`}
                      onClick={() => setShowNieuweRelatie(false)}
                    >
                      Annuleren
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="boekstuknummer">Boekstuknummer (optioneel)</label>
            <input
              type="text"
              id="boekstuknummer"
              value={boekstuknummer}
              onChange={(e) => setBoekstuknummer(e.target.value)}
              placeholder="Boekstuknummer (bijv. 25001)"
            />
          </div>

          {boekstuk === 'inkoop' && (
            <div className={styles.formGroup}>
              <label htmlFor="extern_factuurnummer">Factuurnummer leverancier (optioneel)</label>
              <input
                type="text"
                id="extern_factuurnummer"
                value={externFactuurnummer}
                onChange={(e) => setExternFactuurnummer(e.target.value)}
                placeholder="Factuurnummer van de leverancier"
              />
            </div>
          )}

          <div className={styles.formGroup}>
            <label htmlFor="notitie">Notitie (optioneel)</label>
            <textarea
              id="notitie"
              value={notitie}
              onChange={(e) => setNotitie(e.target.value)}
              rows={3}
              placeholder="Extra notities..."
            />
          </div>

          <div className={styles.formActions}>
            <button
              type="button"
              className={`${styles.btn} ${styles.btnSecondary}`}
              onClick={() => navigate(-1)}
            >
              Annuleren
            </button>
            <button
              type="submit"
              className={`${styles.btn} ${styles.btnPrimary}`}
              disabled={saving}
            >
              {saving ? 'Opslaan...' : 'Opslaan'}
            </button>
          </div>
        </form>
      </div>
    </BoekhoudingLayout>
  );
};

export default BoekingForm;
